class BrimProductGallery extends HTMLElement {
  connectedCallback() {
    this.setupFrame = requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-index]'));
    this.previous = this.querySelector('[data-gallery-previous]');
    this.next = this.querySelector('[data-gallery-next]');
    this.index = 0;
    if (!this.track || this.slides.length < 2) return;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-gallery-index')) this.select(Number(button.dataset.galleryIndex));
      if (button === this.previous) this.select(this.index - 1);
      if (button === this.next) this.select(this.index + 1);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const movement = { ArrowLeft: rtl ? 1 : -1, ArrowRight: rtl ? -1 : 1 };
      if (event.key in movement) {
        event.preventDefault();
        this.select(this.index + movement[event.key]);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.select(event.key === 'Home' ? 0 : this.slides.length - 1);
      }
    }, { signal });

    this.track.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.sync(), 120);
    }, { passive: true, signal });
    this.track.addEventListener('scrollend', () => this.sync(), { signal });
    this.resizeObserver = new ResizeObserver(() => this.select(this.index, false));
    this.resizeObserver.observe(this.track);
    this.updateControls();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.setupFrame);
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    clearTimeout(this.scrollTimer);
  }

  select(index, animate = true) {
    this.index = Math.max(0, Math.min(index, this.slides.length - 1));
    const trackBounds = this.track.getBoundingClientRect();
    const slideBounds = this.slides[this.index].getBoundingClientRect();
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    this.track.scrollTo({
      left: this.track.scrollLeft + (rtl ? slideBounds.right - trackBounds.right : slideBounds.left - trackBounds.left),
      behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant',
    });
    this.updateControls();
  }

  sync() {
    clearTimeout(this.scrollTimer);
    const trackBounds = this.track.getBoundingClientRect();
    const trackCenter = (trackBounds.left + trackBounds.right) / 2;
    let distance = Infinity;
    this.slides.forEach((slide, index) => {
      const bounds = slide.getBoundingClientRect();
      const nextDistance = Math.abs((bounds.left + bounds.right) / 2 - trackCenter);
      if (nextDistance < distance) {
        distance = nextDistance;
        this.index = index;
      }
    });
    this.updateControls();
  }

  updateControls() {
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    this.thumbnails.forEach((thumbnail, index) => {
      if (index === this.index) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    });
    const thumbnail = this.thumbnails[this.index];
    if (!thumbnail) return;
    const strip = thumbnail.parentElement;
    const bounds = thumbnail.getBoundingClientRect();
    const stripBounds = strip.getBoundingClientRect();
    if (bounds.left < stripBounds.left || bounds.right > stripBounds.right) {
      strip.scrollBy({ left: bounds.left - stripBounds.left - (strip.clientWidth - thumbnail.offsetWidth) / 2, behavior: 'instant' });
    }
  }
}

if (!customElements.get('brim-product-gallery')) {
  customElements.define('brim-product-gallery', BrimProductGallery);
}
